<script setup lang="ts">
/**
 * Календарь задач — макет «Календарь» на странице «01 · Ядро».
 *
 * Экран отвечает на один вопрос: что и когда сгорает. Поэтому в сетке только
 * задачи со сроком — без срока задача в календаре не существует, и класть её
 * «на сегодня» значило бы выдумать за человека дату.
 *
 * Задачи те же, что в «Моих»: свои и те, за которыми следишь. Календарь всех
 * задач всех проектов был бы стеной, в которой не найти своё, а разрез по
 * проекту уже есть на доске.
 */
import { AppButton, AppSegmentedControl } from '@vinprop/design-system'
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { dayKey, dueKey, monthGrid, shiftMonth } from '@/features/calendar/month'
import { isOverdue, listMine, type MyIssue } from '@/features/my/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'

/** Сколько задач влезает в клетку до «ещё N» */
const PER_DAY = 4

const { t, locale } = useI18n()

const scope = ref<'mine' | 'watching'>('mine')
const month = ref(new Date())
const issues = ref<MyIssue[]>([])
const isLoading = ref(false)
const error = ref('')

const scopes = computed(() => [
  { value: 'mine' as const, label: t('calendar.scope.mine') },
  { value: 'watching' as const, label: t('calendar.scope.watching') },
])

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    issues.value = await listMine(scope.value === 'watching')
  } catch {
    error.value = t('calendar.error.description')
  } finally {
    isLoading.value = false
  }
}

/** Задачи по дням срока: ключ — местная дата, а не UTC-срез */
const byDay = computed(() => {
  const map = new Map<string, MyIssue[]>()

  for (const issue of issues.value) {
    const key = dueKey(issue.dueDate)

    if (!key) continue

    map.set(key, [...(map.get(key) ?? []), issue])
  }

  return map
})

const weeks = computed(() => monthGrid(month.value, byDay.value))

// Месяц и год порознь: с `year: 'numeric'` русская локаль дописывает «г.»,
// а в заголовке экрана это лишнее слово.
const monthLabel = computed(
  () =>
    `${month.value.toLocaleDateString(locale.value, { month: 'long' })} ${month.value.getFullYear()}`,
)

/** Подписи дней недели берём у самой локали — переводить их вручную незачем */
const weekdays = computed(() => {
  const monday = new Date(2026, 7, 10)

  return Array.from({ length: 7 }, (_, index) =>
    new Date(2026, 7, 10 + index - monday.getDay() + 1).toLocaleDateString(locale.value, {
      weekday: 'short',
    }),
  )
})

const withoutDue = computed(() => issues.value.filter((issue) => !dueKey(issue.dueDate)).length)

const isEmptyMonth = computed(
  () => !isLoading.value && !error.value && weeks.value.every((week) => week.every((d) => !d.items.length)),
)

const today = () => {
  month.value = new Date()
}

const step = (direction: number) => {
  month.value = shiftMonth(month.value, direction)
}

onMounted(load)

watch(scope, load)
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('views.calendar') }]">
    <AppSegmentedControl
      v-model="scope"
      :options="scopes"
      size="small"
      :aria-label="t('calendar.scope.label')"
    />

    <AppButton variant="secondary" size="small" @click="today">
      {{ t('calendar.today') }}
    </AppButton>
  </TrackerToolbar>

  <div class="cal">
    <header class="cal__head">
      <h1 class="cal__month">{{ monthLabel }}</h1>

      <div class="cal__months">
        <button
          type="button"
          class="cal__step"
          :aria-label="t('calendar.prev')"
          @click="step(-1)"
        >
          <ChevronLeft :size="14" />
        </button>
        <button type="button" class="cal__step" :aria-label="t('calendar.next')" @click="step(1)">
          <ChevronRight :size="14" />
        </button>
      </div>

      <span class="cal__spacer" />

      <!-- Задачи без срока в сетке места не имеют, но и молчать о них нельзя:
           иначе календарь выглядит как полный список, которым он не является. -->
      <p v-if="withoutDue" class="cal__note">
        {{ t('calendar.withoutDue', { count: withoutDue }) }}
      </p>
    </header>

    <TrackerEmptyState
      v-if="error"
      :icon="AlertTriangle"
      :title="t('calendar.error.title')"
      :description="error"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <template v-else>
      <div class="cal__weekdays">
        <span v-for="day in weekdays" :key="day" class="cal__weekday">{{ day }}</span>
      </div>

      <div class="cal__grid">
        <div v-for="(week, index) in weeks" :key="index" class="cal__week">
          <div
            v-for="day in week"
            :key="dayKey(day.date)"
            class="cal__day"
            :class="{ 'cal__day--outside': day.outside, 'cal__day--today': day.today }"
          >
            <span class="cal__number">{{ day.day }}</span>

            <RouterLink
              v-for="issue in day.items.slice(0, PER_DAY)"
              :key="issue.id"
              class="cal__task"
              :class="{ 'cal__task--overdue': isOverdue(issue) }"
              :to="`/issue/${issue.key}`"
              :title="issue.title"
            >
              <IssueTypeIcon :type="issue.type" :size="12" />
              <span class="cal__key">{{ issue.key }}</span>
              <span class="cal__title">{{ issue.title }}</span>
            </RouterLink>

            <span v-if="day.items.length > PER_DAY" class="cal__more">
              {{ t('calendar.more', { count: day.items.length - PER_DAY }) }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="isEmptyMonth" class="cal__empty">{{ t('calendar.empty') }}</p>
    </template>
  </div>
</template>

<style scoped>
/* Экран ровно по высоте окна: сетка месяца — это таблица, и прокручивать её
   целиком незачем, а клетки тянутся по остатку. */
.cal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 48px);
  padding: 20px 24px;
}

.cal__head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cal__month {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
  text-transform: capitalize;
}

.cal__months {
  display: flex;
  gap: 4px;
}

.cal__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.cal__step:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

.cal__spacer {
  flex: 1;
}

.cal__note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.cal__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}

.cal__weekday {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cal__grid {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  min-height: 0;
}

.cal__week {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  min-height: 0;
}

.cal__day {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.cal__day--outside {
  background: var(--color-bg-muted);
}

.cal__day--today {
  border-color: var(--color-primary);
}

.cal__number {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.cal__day--outside .cal__number {
  color: var(--color-text-muted);
}

.cal__task {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 3px 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  text-decoration: none;
}

.cal__task:hover {
  background: var(--color-border);
}

.cal__key {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
}

/* Просроченное видно ключом: заливка всей плашки красным на пяти задачах
   подряд превращает месяц в тревогу. */
.cal__task--overdue .cal__key {
  color: var(--color-danger);
}

.cal__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal__more {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}

.cal__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
